// Barra de cambios sin guardar: sube desde abajo cuando hay diferencias, deja verlas (antes -> ahora), descartarlas
// o guardarlas (Ctrl+S). Si la persona se va a otra pantalla con cambios, pregunta que hacer con ellos.
// Cada formulario la monta; solo la primera se dibuja y junta los cambios de todos.
import { useCallback, useEffect, useId, useRef, useState, useSyncExternalStore } from 'react';
import { createPortal } from 'react-dom';
import { useBlocker } from 'react-router-dom';

import {
  descartarTodo,
  desmontarBarra,
  estadoCambios,
  guardarTodo,
  invalidoDe,
  montarBarra,
  pedirAviso,
  suscribirCambios,
  textoCambios,
  totalDe,
} from '../lib/cambiosPendientes';
import { reducedMotion } from '../lib/spring';
import type { CambiosPendientes } from '../lib/useCambiosPendientes';
import { MODULOS } from '../layout/menu';

const useEstado = () => useSyncExternalStore(suscribirCambios, estadoCambios, estadoCambios);

// Nombre legible del destino de una navegacion ("Casos", "Agenda"...).
export function nombreDestino(ruta: string) {
  let mejor: { r: string; n: string } | null = null;
  for (const m of MODULOS)
    for (const s of m.secciones)
      for (const r of [s.ruta, ...(s.tambien ?? [])]) {
        const base = r.replace(/\/$/, '');
        if ((ruta === base || ruta.startsWith(base + '/')) && (!mejor || base.length > mejor.r.length)) mejor = { r: base, n: s.nombre };
      }
  if (mejor) return mejor.n;
  if (ruta.startsWith('/perfil')) return 'Mi perfil';
  return 'otra pantalla';
}

export function BarraCambios() {
  const id = useId();
  const e = useEstado();
  useEffect(() => {
    montarBarra(id);
    return () => desmontarBarra(id);
  }, [id]);
  if (e.barras[0] !== id) return null;
  return <Barra />;
}

function Barra() {
  const e = useEstado();
  const total = totalDe(e.registros);
  const invalido = invalidoDe(e.registros);
  const [ver, setVer] = useState(false);
  const [estado, setEstado] = useState<'normal' | 'guardando' | 'ok'>('normal');
  const ref = useRef<HTMLDivElement>(null);
  const visible = total > 0 || !!e.aviso || estado === 'ok';

  useEffect(() => {
    if (!total) setVer(false);
  }, [total]);

  const guardar = useCallback(async () => {
    const s = estadoCambios();
    if (!totalDe(s.registros) || invalidoDe(s.registros)) return false;
    setEstado('guardando');
    const ok = await guardarTodo(s.registros);
    setEstado(ok ? 'ok' : 'normal');
    if (ok) {
      setVer(false);
      window.setTimeout(() => setEstado((x) => (x === 'ok' ? 'normal' : x)), 1600);
    }
    return ok;
  }, []);

  // Ctrl/Cmd+S guarda; al cerrar o recargar la pestana con cambios, el navegador pregunta.
  useEffect(() => {
    const tecla = (ev: KeyboardEvent) => {
      if (!(ev.ctrlKey || ev.metaKey) || ev.key.toLowerCase() !== 's') return;
      if (!totalDe(estadoCambios().registros)) return;
      ev.preventDefault();
      void guardar();
    };
    window.addEventListener('keydown', tecla);
    return () => window.removeEventListener('keydown', tecla);
  }, [guardar]);
  useEffect(() => {
    if (!total) return;
    const fn = (ev: BeforeUnloadEvent) => {
      ev.preventDefault();
      ev.returnValue = '';
    };
    window.addEventListener('beforeunload', fn);
    return () => window.removeEventListener('beforeunload', fn);
  }, [total]);

  // Pequeno empujon al aparecer la pregunta, para que se note.
  useEffect(() => {
    if (!e.aviso || !ref.current || reducedMotion()) return;
    ref.current.animate([{ transform: 'translateY(0)' }, { transform: 'translateY(-6px)' }, { transform: 'translateY(0)' }], { duration: 320, easing: 'cubic-bezier(.2,.9,.3,1.2)' });
  }, [e.aviso]);

  const varios = e.registros.filter((r) => r.cambios.length).length > 1;
  const cuantos = total === 1 ? 'el cambio' : `los ${total} cambios`;

  let contenido;
  if (estado === 'ok') contenido = <span className="cp-b-ok">✓ Guardado</span>;
  else if (e.aviso) {
    const a = e.aviso;
    contenido = (
      <>
        <span className="cp-b-txt">
          <i className="cp-punto" aria-hidden="true" />
          <span>
            {a.destino === 'cerrar' ? 'Antes de cerrar' : `Antes de ir a ${a.destino}`}: ¿qué hacemos con {cuantos}?
          </span>
        </span>
        <div className="cp-b-acc">
          <button
            type="button"
            className="cp-bb"
            onClick={() => {
              descartarTodo();
              pedirAviso(null);
              a.ir();
            }}
          >
            {a.destino === 'cerrar' ? 'Descartar y cerrar' : 'Descartar e ir'}
          </button>
          <button
            type="button"
            className="cp-bb prim"
            disabled={!!invalido || estado === 'guardando'}
            title={invalido ? `Corrige: ${invalido}` : undefined}
            onClick={async () => {
              const ok = await guardar();
              pedirAviso(null);
              if (ok) a.ir();
              else a.quedar();
            }}
          >
            {estado === 'guardando' ? 'Guardando…' : a.destino === 'cerrar' ? 'Guardar y cerrar' : 'Guardar e ir'}
          </button>
          <button
            type="button"
            className="cp-bb"
            autoFocus
            onClick={() => {
              pedirAviso(null);
              a.quedar();
            }}
          >
            Seguir aquí
          </button>
        </div>
      </>
    );
  } else if (total) {
    contenido = (
      <>
        {ver ? (
          <div className="cp-lista" id="cp-lista">
            <p className="lbl">Lo que cambia</p>
            {e.registros
              .filter((r) => r.cambios.length)
              .map((r) =>
                r.cambios.map((c) => (
                  <div className="cp-l-f" key={r.id + c.id}>
                    <span>{varios ? `${r.titulo} · ${c.t}` : c.t}</span>
                    <span>
                      <s>{c.antes}</s> → <b>{c.ahora}</b>
                    </span>
                  </div>
                )),
              )}
          </div>
        ) : null}
        <span className="cp-b-txt" role="status">
          <i className="cp-punto" aria-hidden="true" />
          <span>
            <b>{textoCambios(total)}</b> sin guardar
          </span>
          {invalido ? <span className="cp-b-inv">· Corrige: {invalido}</span> : null}
        </span>
        <div className="cp-b-acc">
          <button type="button" className="cp-bb" aria-expanded={ver} aria-controls="cp-lista" onClick={() => setVer(!ver)}>
            {ver ? 'Ocultar' : 'Ver cambios'}
          </button>
          <button type="button" className="cp-bb" onClick={() => descartarTodo()} disabled={estado === 'guardando'}>
            Descartar
          </button>
          <button type="button" className="cp-bb prim" disabled={!!invalido || estado === 'guardando'} title={invalido ? `Corrige: ${invalido}` : undefined} onClick={() => void guardar()}>
            {estado === 'guardando' ? 'Guardando…' : 'Guardar'} <kbd>Ctrl S</kbd>
          </button>
        </div>
      </>
    );
  }

  return (
    <>
      {total > 0 ? <Guardia /> : null}
      {createPortal(
        <div ref={ref} className={`cp-barra${visible ? ' visible' : ''}${estado === 'ok' ? ' ok' : ''}${e.aviso ? ' aviso' : ''}`} role="region" aria-label="Cambios sin guardar" aria-hidden={!visible}>
          {contenido}
        </div>,
        document.body,
      )}
    </>
  );
}

// Detiene la navegacion dentro de la Consola mientras haya cambios y la convierte en pregunta.
// Solo existe una a la vez (React Router admite un solo bloqueador).
function Guardia() {
  const b = useBlocker(({ currentLocation, nextLocation }) => currentLocation.pathname !== nextLocation.pathname && totalDe(estadoCambios().registros) > 0);
  const ultimo = useRef(b);
  ultimo.current = b;
  const destino = b.state === 'blocked' ? b.location.pathname : null;
  useEffect(() => {
    if (destino === null) return;
    pedirAviso({ destino: nombreDestino(destino), ir: () => ultimo.current.proceed?.(), quedar: () => ultimo.current.reset?.() });
  }, [destino]);
  useEffect(() => () => pedirAviso(null), []);
  return null;
}

// "Antes: X · Deshacer" bajo un campo que cambio.
export function Antes({ c, campo }: { c: CambiosPendientes; campo: string }) {
  const x = c.cambiado(campo);
  if (!x) return null;
  return (
    <span className="cp-antes">
      <span>
        Antes: <b>{x.antes}</b>
      </span>
      <button type="button" onClick={() => c.deshacer(campo)}>
        ↶ Deshacer
      </button>
    </span>
  );
}

// Cerrar un dialogo con cambios (Esc o clic afuera): primero preguntar en la barra.
export function cerrarConCambios(c: CambiosPendientes, cerrar: () => void) {
  if (!c.sucio) return cerrar();
  pedirAviso({ destino: 'cerrar', ir: cerrar, quedar: () => {} });
}
